import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Endpoint {
  side: 'left' | 'right'
  index: number
}

interface Props {
  /** Right choice index per left choice; -1 means an unresolved answer. */
  matches: number[]
  rightCount: number
  interactive?: boolean
}

const WIDTH = 256
const ROW_HEIGHT = 48
const NODE_RADIUS = 16
const LEFT_X = 24
const RIGHT_X = WIDTH - LEFT_X
const centerY = (index: number) => index * ROW_HEIGHT + ROW_HEIGHT / 2

export function PairingAnswerDiagram({ matches, rightCount, interactive = true }: Props) {
  const { t } = useTranslation('coaching')
  const [hovered, setHovered] = useState<Endpoint | null>(null)
  const [focused, setFocused] = useState<Endpoint | null>(null)
  const [selected, setSelected] = useState<Endpoint | null>(null)
  const active = interactive ? hovered ?? focused ?? selected : null
  const height = Math.max(matches.length, rightCount) * ROW_HEIGHT
  if (height === 0) return null
  const isActiveMatch = (left: number, right: number) => active != null && (
    active.side === 'left' ? active.index === left : active.index === right
  )
  const connections = matches.flatMap((right, left) => right >= 0 && right < rightCount
    ? [{ left, right, highlighted: isActiveMatch(left, right) }]
    : [])
  // Draw the highlighted connection last so it stays legible at crossings.
  connections.sort((a, b) => Number(a.highlighted) - Number(b.highlighted))
  const toggle = (endpoint: Endpoint) => setSelected((previous) =>
    previous?.side === endpoint.side && previous.index === endpoint.index ? null : endpoint)

  return (
    <figure className="m-0 w-full max-w-72 rounded-xl border border-border bg-background p-4" aria-label={t('gradingMode.pairingDiagram')}>
      <figcaption className="mb-1 flex justify-between text-xs font-medium text-muted-foreground">
        <span>{t('gradingMode.pairingLeft')}</span>
        <span>{t('gradingMode.pairingRight')}</span>
      </figcaption>
      <svg className="block w-full overflow-visible" viewBox={`0 0 ${WIDTH} ${height}`} onMouseLeave={interactive ? () => setHovered(null) : undefined}>
        <g aria-hidden="true" fill="none">
          {connections.map(({ left, right, highlighted }) => {
            const start = LEFT_X + NODE_RADIUS
            const end = RIGHT_X - NODE_RADIUS
            const path = `M ${start} ${centerY(left)} L ${end} ${centerY(right)}`
            return (
              <g key={left}>
                <path d={path} className="stroke-background" strokeWidth={7} />
                <path d={path} data-left={left + 1} data-right={right + 1}
                  className={!interactive ? 'stroke-red-600' : highlighted ? 'stroke-primary' : 'stroke-muted-foreground'}
                  strokeWidth={highlighted ? 3 : 2} strokeOpacity={!interactive || highlighted ? 1 : 0.65} strokeLinecap="round"
                  opacity={active == null || highlighted ? 1 : 0.15}
                />
              </g>
            )
          })}
        </g>
        {(['left', 'right'] as const).flatMap((side) =>
          Array.from({ length: side === 'left' ? matches.length : rightCount }, (_, index) => {
            const x = side === 'left' ? LEFT_X : RIGHT_X
            const y = centerY(index)
            const endpoint = { side, index }
            const highlighted = connections.some((connection) => connection.highlighted && (side === 'left' ? connection.left === index : connection.right === index))
            return (
              <g key={`${side}-${index}`} role={interactive ? 'button' : undefined} tabIndex={interactive ? 0 : undefined}
                className={interactive ? 'group cursor-pointer outline-none' : ''}
                aria-label={interactive ? t(side === 'left' ? 'gradingMode.pairingLeftOption' : 'gradingMode.pairingRightOption', { no: index + 1 }) : undefined}
                aria-pressed={interactive ? selected?.side === side && selected.index === index : undefined}
                onMouseEnter={interactive ? () => setHovered(endpoint) : undefined} onMouseLeave={interactive ? () => setHovered(null) : undefined}
                onFocus={interactive ? () => setFocused(endpoint) : undefined} onBlur={interactive ? () => setFocused(null) : undefined}
                onClick={interactive ? () => toggle(endpoint) : undefined}
                onKeyDown={interactive ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    toggle(endpoint)
                  } else if (event.key === 'Escape') setSelected(null)
                } : undefined}
              >
                <circle cx={x} cy={y} r={22} className="fill-transparent" />
                <circle cx={x} cy={y} r={20} className="fill-none stroke-ring opacity-0 group-focus-visible:opacity-100" strokeWidth={2} />
                <circle cx={x} cy={y} r={NODE_RADIUS}
                  className={highlighted ? 'fill-accent stroke-primary' : 'fill-background stroke-border'} strokeWidth={highlighted ? 2 : 1.5} />
                <text x={x} y={y} dy="0.35em" textAnchor="middle"
                  className={`pointer-events-none text-sm font-semibold ${side === 'left' ? 'fill-black' : 'fill-red-600'}`}>
                  {index + 1}
                </text>
              </g>
            )
          }),
        )}
      </svg>
    </figure>
  )
}
